import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { IndicationCacheDto } from "../models/indication-cache.model";
import { SessionCacheApiService } from "./session-cache-api.service";
import { LoginDetailsStorageService } from "./login-details-storage.service";

/**
 * App sessionStorage keys for indication cache (bridged — not stored in browser).
 * API field names differ for version, name insured, draft, and building — see mappings below.
 */
export const INDICATION_DETAILS_STORAGE_KEYS = new Set([
  "indicationId",
  "indicationNo",
  "indicationStatus",
  "indicationAddressId",
  "versionNo",
  "indicationDraftStatus",
  "buildingid",
  "policyTypeId",
]);

const CACHE_TYPE = "indication";

@Injectable({
  providedIn: "root",
})
export class IndicationDetailsStorageService {
  private readonly sessionCacheApi = inject(SessionCacheApiService);
  private readonly loginDetailsStorage = inject(LoginDetailsStorageService);
  private readonly values = new Map<string, string>();
  private static bridgeInstalled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private persistEnabled = true;
  private loaded = false;

  purgeNativeIndicationKeysFromBrowser(): void {
    if (typeof window === "undefined") {
      return;
    }
    const nativeRemove = Storage.prototype.removeItem;
    INDICATION_DETAILS_STORAGE_KEYS.forEach((key) => {
      try {
        nativeRemove.call(window.sessionStorage, key);
      } catch {
        /* ignore */
      }
    });
  }

  /** Chain after TempDetailsStorageService.initSessionStorageBridge(). */
  initSessionStorageBridge(): void {
    if (IndicationDetailsStorageService.bridgeInstalled || typeof window === "undefined") {
      return;
    }

    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const wrappedGetItem = storageProto.getItem;
    const wrappedSetItem = storageProto.setItem;
    const wrappedRemoveItem = storageProto.removeItem;
    const store = this;

    storageProto.getItem = function (key: string): string | null {
      if (this === window.sessionStorage && INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
        return store.getItem(key);
      }
      return wrappedGetItem.call(this, key);
    };

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage && INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
        store.setItem(key, value);
        return;
      }
      wrappedSetItem.call(this, key, value);
    };

    storageProto.removeItem = function (key: string): void {
      if (this === window.sessionStorage && INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
        store.removeItem(key);
        return;
      }
      wrappedRemoveItem.call(this, key);
    };

    IndicationDetailsStorageService.bridgeInstalled = true;
  }

  getItem(key: string): string | null {
    if (!INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
      return null;
    }
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.set(key, String(value));
    this.schedulePersist();
  }

  removeItem(key: string): void {
    if (!INDICATION_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.delete(key);
    this.schedulePersist();
  }

  clear(): void {
    this.values.clear();
    this.loaded = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  disablePersist(): void {
    this.persistEnabled = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  enablePersist(): void {
    this.persistEnabled = true;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getCacheSessionId(): string | null {
    return this.loginDetailsStorage.getCacheSessionId();
  }

  buildCachePayload(overrides: Partial<IndicationCacheDto> = {}): IndicationCacheDto {
    const payload: IndicationCacheDto = {
      indicationId: this.toCacheInt(this.getItem("indicationId")),
      indicationNo: this.getItem("indicationNo") ?? "",
      indicationStatus: this.getItem("indicationStatus") ?? "",
      indicationAddressId: this.toCacheAddressId(this.getItem("indicationAddressId")),
      indicationNameInsured: this.readSessionField("nameInsured") ?? "",
      indicationVersionNo: this.toCacheInt(this.getItem("versionNo")),
      policyTypeId: this.toCacheInt(this.getItem("policyTypeId")),
      indicationDraft: this.extractIndicationDraft(),
      indicationBuildingId: this.getItem("buildingid") ?? "",
      ...overrides,
    };

    return {
      indicationId: this.toCacheInt(payload.indicationId),
      indicationNo: payload.indicationNo ?? "",
      indicationStatus: payload.indicationStatus ?? "",
      indicationAddressId: this.toCacheInt(payload.indicationAddressId),
      indicationNameInsured: payload.indicationNameInsured ?? "",
      indicationVersionNo: this.toCacheInt(payload.indicationVersionNo),
      policyTypeId: this.toCacheInt(payload.policyTypeId),
      indicationDraft: this.toCacheInt(payload.indicationDraft),
      indicationBuildingId: payload.indicationBuildingId ?? "",
    };
  }

  applyFromCacheDto(data: IndicationCacheDto): void {
    if (data.indicationId !== undefined && data.indicationId !== null && data.indicationId !== 0) {
      this.values.set("indicationId", String(data.indicationId));
    }
    if (data.indicationNo) {
      this.values.set("indicationNo", String(data.indicationNo));
    }
    if (data.indicationStatus) {
      this.values.set("indicationStatus", String(data.indicationStatus));
    }
    if (data.indicationAddressId !== undefined && data.indicationAddressId !== null) {
      this.values.set(
        "indicationAddressId",
        this.formatAddressIdForSession(data.indicationAddressId)
      );
    }
    if (data.policyTypeId !== undefined && data.policyTypeId !== null && data.policyTypeId !== 0) {
      this.values.set("policyTypeId", String(data.policyTypeId));
    }
    if (data.indicationBuildingId) {
      this.values.set("buildingid", String(data.indicationBuildingId));
    }

    if (data.indicationDraft !== undefined && data.indicationDraft !== null) {
      this.values.set(
        "indicationDraftStatus",
        JSON.stringify({ indicationDraft: data.indicationDraft })
      );
    }

    if (data.indicationNameInsured) {
      this.writeSessionField("nameInsured", String(data.indicationNameInsured));
    }
    if (data.indicationVersionNo !== undefined && data.indicationVersionNo !== null) {
      this.values.set("versionNo", String(data.indicationVersionNo));
    }

    this.loaded = true;
  }

  loadFromCache(sessionId?: string | number): Observable<boolean> {
    const token = this.loginDetailsStorage.getItem("token");
    const sid = sessionId ?? this.getCacheSessionId();

    if (!token || !sid) {
      return of(false);
    }

    return this.sessionCacheApi.get<IndicationCacheDto>(CACHE_TYPE, sid).pipe(
      tap((data) => {
        if (data) {
          this.applyFromCacheDto(data);
        }
      }),
      map((data) => !!data),
      catchError(() => of(false))
    );
  }

  saveToCache(
    overrides: Partial<IndicationCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<IndicationCacheDto | null> {
    const sessionId = sessionIdOverride ?? this.getCacheSessionId();
    if (!sessionId) {
      return of(null);
    }

    const payload = this.buildCachePayload(overrides);
    this.applyFromCacheDto(payload);

    return this.sessionCacheApi.set(CACHE_TYPE, sessionId, payload).pipe(
      map(() => payload),
      catchError(() => of(payload))
    );
  }

  removeFromCache(sessionId?: string | number): Observable<unknown> {
    const sid = sessionId ?? this.getCacheSessionId();
    if (!sid) {
      return of(null);
    }
    return this.sessionCacheApi.remove(CACHE_TYPE, sid);
  }

  toCacheInt(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  private readSessionField(key: string): string | null {
    if (typeof window === "undefined") {
      return null;
    }
    return window.sessionStorage.getItem(key);
  }

  private writeSessionField(key: string, value: string): void {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(key, value);
  }

  private toCacheAddressId(value: string | null): number {
    if (!value) {
      return 0;
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return this.toCacheInt(parsed[0]);
      }
      return this.toCacheInt(parsed);
    } catch {
      return this.toCacheInt(value);
    }
  }

  private formatAddressIdForSession(addressId: number): string {
    if (!addressId) {
      return "";
    }
    const existing = this.getItem("indicationAddressId");
    if (existing?.startsWith("[")) {
      return existing;
    }
    return JSON.stringify([addressId]);
  }

  private extractIndicationDraft(): number {
    const raw = this.getItem("indicationDraftStatus");
    if (!raw) {
      return 0;
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null && "indicationDraft" in parsed) {
        return this.toCacheInt((parsed as { indicationDraft: number }).indicationDraft);
      }
      return this.toCacheInt(parsed);
    } catch {
      return this.toCacheInt(raw);
    }
  }

  private schedulePersist(): void {
    if (!this.persistEnabled) {
      return;
    }
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const sessionId = this.getCacheSessionId();
      if (sessionId) {
        this.saveToCache().subscribe();
      }
    }, 300);
  }
}
